/**
 * Os calendários que cada um assina, e o que cada um decide dividir.
 *
 * Porte de `lib/features/calendar/data/services/partner_calendar_service.dart`:
 * o `PartnerCalendar`, o `CalendarFeedEvent` e o `CalendarLists`.
 *
 * O que não existe aqui, de propósito, é o endereço do calendário. Um link
 * secreto de .ics é uma credencial — quem tem o link lê a agenda inteira para
 * sempre — então ele fica no servidor, e o aparelho só descobre se existe um.
 * O `publicShape` de `server/src/calendars.js` já corta o campo antes de
 * responder; este arquivo nem tem onde guardá-lo.
 */

/** O que o servidor chama de `kind`: uma assinatura de fora, ou uma lista do app. */
export type TipoDeCalendario = 'ics' | 'local';

export interface CalendarioDaCasa {
  id: string;
  nome: string;
  /** O `#RRGGBB` escolhido na hora de criar. Pode vir vazio ou torto. */
  cor: string;
  tipo: TipoDeCalendario;
  compartilhado: boolean;
  meu: boolean;
  /** Se já existe um link guardado no servidor. Nunca qual é. */
  temFeed: boolean;
}

/** O `isFeed` de lá. */
export const eAssinatura = (c: CalendarioDaCasa): boolean => c.tipo === 'ics';

export interface EventoDeCalendario {
  titulo: string;
  inicio: Date;
  fim: Date | null;
  diaInteiro: boolean;
  lugar: string;
  descricao: string;
}

/** Os dois lados da lista: o que é meu, e o que a outra pessoa dividiu comigo. */
export interface ListasDeCalendarios {
  meus: CalendarioDaCasa[];
  compartilhados: CalendarioDaCasa[];
}

/** O `CalendarLists.empty()`. */
export const LISTAS_VAZIAS: ListasDeCalendarios = { meus: [], compartilhados: [] };

/** O `isEmpty` de lá. */
export const semCalendarios = (l: ListasDeCalendarios): boolean =>
  l.meus.length === 0 && l.compartilhados.length === 0;

/**
 * As seis cores do `_palette` do editor, em hexa e não em token.
 *
 * Elas viajam para o servidor como texto e voltam de lá do mesmo jeito, então
 * o que o app grava tem de ser exatamente o que o Flutter grava — um
 * `var(--aurora-agua)` guardado no KV não pintaria nada no celular.
 */
export const PALETA_DE_CALENDARIO = [
  '#7EE8D2',
  '#FF6F91',
  '#B79CED',
  '#F5C97B',
  '#4A90D9',
  '#F4C2C2',
] as const;

/** A cor de quem não escolheu nenhuma: o `AppColors.auroraTeal` do `_tint`. */
export const COR_PADRAO_DE_CALENDARIO = '#7EE8D2';

/**
 * O `_tint`: um `#RRGGBB` de seis dígitos, ou a cor padrão.
 *
 * O `int.tryParse(raw, radix: 16)` de lá aceita qualquer coisa que caiba em
 * base 16, e só depois confere o comprimento; um `#12` vira o padrão, e um
 * `azul` também.
 */
export function corDoCalendario(cor: string): string {
  const cru = cor.startsWith('#') ? cor.slice(1) : cor;
  if (cru.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(cru)) return COR_PADRAO_DE_CALENDARIO;
  return `#${cru}`;
}

/** Os meses abreviados do `_EventRow`. */
const MESES = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

/** O `_when`: "19 ago", e a hora junto quando o evento não é do dia inteiro. */
export function quandoDoEvento(e: EventoDeCalendario): string {
  const dia = `${e.inicio.getDate()} ${MESES[e.inicio.getMonth()]}`;
  if (e.diaInteiro) return dia;
  const hh = String(e.inicio.getHours()).padStart(2, '0');
  const mm = String(e.inicio.getMinutes()).padStart(2, '0');
  return `${dia} · ${hh}:${mm}`;
}

const texto = (v: unknown, padrao = ''): string =>
  v === null || v === undefined ? padrao : String(v);

/** O `PartnerCalendar.fromJson`: sem `id` não é calendário nenhum. */
export function calendarioDeMapa(dados: unknown): CalendarioDaCasa | null {
  if (typeof dados !== 'object' || dados === null) return null;
  const j = dados as Record<string, unknown>;
  const id = texto(j.id);
  if (!id) return null;
  return {
    id,
    nome: texto(j.name),
    cor: texto(j.color),
    tipo: texto(j.kind, 'local') === 'ics' ? 'ics' : 'local',
    compartilhado: j.shared === true,
    meu: j.mine === true,
    temFeed: j.hasFeed === true,
  };
}

/** O `CalendarFeedEvent.fromJson`: sem começo não dá para colocar em lugar nenhum. */
export function eventoDeCalendarioDeMapa(dados: unknown): EventoDeCalendario | null {
  if (typeof dados !== 'object' || dados === null) return null;
  const j = dados as Record<string, unknown>;
  const inicio = new Date(texto(j.start));
  if (Number.isNaN(inicio.getTime())) return null;
  const fim = new Date(texto(j.end));
  return {
    titulo: texto(j.title) || 'Sem título',
    inicio,
    fim: Number.isNaN(fim.getTime()) ? null : fim,
    diaInteiro: j.allDay === true,
    lugar: texto(j.location),
    descricao: texto(j.description),
  };
}

function listaDeCalendarios(cru: unknown): CalendarioDaCasa[] {
  if (!Array.isArray(cru)) return [];
  const fora: CalendarioDaCasa[] = [];
  for (const item of cru) {
    const c = calendarioDeMapa(item);
    if (c) fora.push(c);
  }
  return fora;
}

/** O `_lists` do serviço. */
export function listasDeCalendariosDeMapa(dados: unknown): ListasDeCalendarios {
  if (typeof dados !== 'object' || dados === null) return LISTAS_VAZIAS;
  const j = dados as Record<string, unknown>;
  return { meus: listaDeCalendarios(j.mine), compartilhados: listaDeCalendarios(j.partner) };
}

/** Os eventos de um calendário, já em ordem de quando acontecem. */
export function eventosDeCalendarioDeMapa(dados: unknown): EventoDeCalendario[] {
  if (typeof dados !== 'object' || dados === null) return [];
  const cru = (dados as Record<string, unknown>).events;
  if (!Array.isArray(cru)) return [];
  const fora: EventoDeCalendario[] = [];
  for (const item of cru) {
    const e = eventoDeCalendarioDeMapa(item);
    if (e) fora.push(e);
  }
  return fora.sort((a, b) => a.inicio.getTime() - b.inicio.getTime());
}
